/**
 * Porcupine KWS Implementation
 * 
 * Uses @picovoice/porcupine-web for hardware-accelerated wake word detection.
 * Falls back to TextFallbackKWS if Porcupine is not available or fails to initialize.
 */

import { KWSAdapter } from './KWSAdapter.js';

export class PorcupineKWS extends KWSAdapter {
    constructor() {
        super();
        this.porcupine = null;
        this.isInitialized = false;
        this.isListening = false;
        this.audioContext = null;
        this.audioWorkletNode = null;
        this.onDetectCallback = null;
        this.lastScore = 0;
        this.wakePhrase = 'hey chef';
    }

    async init(options) {
        try {
            // Check if Porcupine key is available
            const porcupineKey = process.env.REACT_APP_PORCUPINE_KEY;
            if (!porcupineKey) {
                console.warn('🎤 PorcupineKWS: REACT_APP_PORCUPINE_KEY not found, falling back to text-based KWS');
                return false;
            }

            // Dynamic import of Porcupine
            const { Porcupine } = await import('@picovoice/porcupine-web');
            
            // Initialize Porcupine with built-in "hey chef" keyword
            this.porcupine = await Porcupine.create(
                porcupineKey,
                ['hey chef'], // Built-in keyword
                {
                    processErrorCallback: (error: any) => {
                        console.error('🎤 PorcupineKWS: Processing error:', error);
                    }
                }
            );

            this.wakePhrase = options.wakePhrase || 'hey chef';
            this.isInitialized = true;
            
            console.log('🎤 PorcupineKWS: Initialized successfully');
            return true;
        } catch (error) {
            console.error('🎤 PorcupineKWS: Initialization failed:', error);
            return false;
        }
    }

    async start() {
        if (!this.isInitialized || !this.porcupine) {
            console.error('🎤 PorcupineKWS: Not initialized');
            return false;
        }

        try {
            // Get AudioContext from MicManager
            const micManager = await import('../MicManager.js');
            this.audioContext = micManager.default.getAudioContext();
            
            if (!this.audioContext) {
                console.error('🎤 PorcupineKWS: No AudioContext available');
                return false;
            }

            // Create audio worklet for processing
            await this.audioContext.audioWorklet.addModule('/kws-processor.js');
            
            this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'kws-processor', {
                processorOptions: {
                    porcupine: this.porcupine,
                    onDetect: (score: number) => {
                        this.lastScore = score;
                        if (this.onDetectCallback) {
                            this.onDetectCallback({
                                phrase: this.wakePhrase,
                                score: score,
                                timestamp: Date.now()
                            });
                        }
                    }
                }
            });

            // Connect to microphone source
            const mediaStreamSource = micManager.default.createMediaStreamSource();
            mediaStreamSource.connect(this.audioWorkletNode);

            this.isListening = true;
            console.log('🎤 PorcupineKWS: Started listening');
            return true;
        } catch (error) {
            console.error('🎤 PorcupineKWS: Failed to start:', error);
            return false;
        }
    }

    async stop() {
        try {
            if (this.audioWorkletNode) {
                this.audioWorkletNode.disconnect();
                this.audioWorkletNode = null;
            }

            this.isListening = false;
            console.log('🎤 PorcupineKWS: Stopped listening');
            return true;
        } catch (error) {
            console.error('🎤 PorcupineKWS: Failed to stop:', error);
            return false;
        }
    }

    async destroy() {
        try {
            await this.stop();
            
            if (this.porcupine) {
                this.porcupine.release();
                this.porcupine = null;
            }

            this.isInitialized = false;
            this.onDetectCallback = null;
            
            console.log('🎤 PorcupineKWS: Destroyed');
            return true;
        } catch (error) {
            console.error('🎤 PorcupineKWS: Failed to destroy:', error);
            return false;
        }
    }

    async setWakePhrase(phrase) {
        this.wakePhrase = phrase;
        // Note: Porcupine uses built-in keywords, so we can't change the phrase dynamically
        // This would require reinitializing with a different keyword
        console.log('🎤 PorcupineKWS: Wake phrase set to:', phrase);
        return true;
    }

    isReady() {
        return this.isInitialized && this.porcupine !== null;
    }

    getLastScore() {
        return this.lastScore;
    }

    onDetect(callback) {
        this.onDetectCallback = callback;
    }

    removeDetectCallback() {
        this.onDetectCallback = null;
    }
}

export default PorcupineKWS;
